// FILE LOCATION: frontend/src/view/prospecting/ExecutivesModalProspecting.jsx

import React, { useEffect, useRef, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import toastr from "../../../utils/toast";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { contact_details } from "../../../utils/sweetalert";

// Seniority tabs: 10 = Founder, 7 = Partner, 9 = C-Suite, 8 = VP, 6 = Director
const SENIORITY_TABS = [
  { id: 10, label: "Founders", countKey: "founder" },
  { id: 7, label: "Partners", countKey: "partner" },
  { id: 9, label: "C-Suite", countKey: "cs" },
  { id: 8, label: "VPs", countKey: "vp" },
  { id: 6, label: "Directors", countKey: "dir" },
];

const DEFAULT_SENIORITY = 9; // C-Suite opens by default

function ExecutivesModalProspecting({ onClose, fqdn, companyName }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();

  const [seniority, setSeniority] = useState(DEFAULT_SENIORITY);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50; // fixed
  const [executives, setExecutives] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [executiveCounts, setExecutiveCounts] = useState({
    founder: null,
    partner: null,
    cs: null,
    vp: null,
    dir: null,
  });

  // Prevent double-fire on mount (React 18 StrictMode double-invoke)
  const mountedRef = useRef(false);
  // Track in-flight fetch so tab clicks don't stack
  const fetchingRef = useRef(false);

  // ── On open: fetch counts first (sequential), then fetch default tab data ──
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    if (!fqdn) return;
    // Sequential: counts first, then executives — avoids parallel burst to Lusha
    fetchCountsSequential().then(() => {
      fetchData(DEFAULT_SENIORITY, 0);
    });
  }, []); // empty — runs once on mount only

  // ── Counts: sequential with small delay to avoid Lusha rate limit ──────────
  const fetchCountsSequential = async () => {
    const seniorityMap = [
      { id: 10, key: "founder" },
      { id: 7, key: "partner" },
      { id: 9, key: "cs" },
      { id: 8, key: "vp" },
      { id: 6, key: "dir" },
    ];

    // Fire all 5 as parallel but catch individually so one failure ≠ all fail.
    // If Lusha rate-limits, switch to sequential version below.
    try {
      setGlobalLoading(true);
      const results = await Promise.all(
        seniorityMap.map(({ id }) =>
          axios
            .post(
              `${API_BASE_URL}/lusha/get_executives_count_prospecting`,
              { fqdn, seniority: id }, // pass single seniority per call
              { headers: { Authorization: `Bearer ${token}` } },
            )
            .then((r) => r.data?.totalResults || 0)
            .catch(() => 0),
        ),
      );

      const counts = {};
      seniorityMap.forEach(({ key }, idx) => {
        counts[key] = results[idx];
      });
      setExecutiveCounts(counts);
      setGlobalLoading(false);
    } catch (err) {
      console.error("fetchCounts error:", err);
    }
  };

  // ── Fetch executives for a given seniority + page ─────────────────────────
  const fetchData = async (seniorityId, page) => {
    if (fetchingRef.current) return; // prevent stacking
    fetchingRef.current = true;
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/lusha/get_executives_prospecting`,
        { fqdn, seniority: seniorityId, page, size: pageSize },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status === 200) {
        if (!response.data.result?.length) {
          toastr.error("No executives found for this level");
        }
        setRequestId(response.data.requestId || "");
        setExecutives(response.data.result || []);
        setRowCount(response.data.totalResults || 0);
      }
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        const msg = data?.error || "Please wait a few seconds and try again.";
        if (status === 400) toastr.warning("Missing or invalid parameters");
        else if (status === 404) toastr.warning("Company domain not found");
        else if (status === 429)
          toastr.warning(
            "Too many requests — please wait a moment and try again.",
          );
        else if (status === 504)
          toastr.warning("Third-party API took too long, please retry");
        else toastr.error(msg);
      } else if (error.request) {
        toastr.error("No response from server. Check your network connection.");
      } else {
        toastr.error("Request setup failed: " + error.message);
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  // ── Tab click: change seniority, reset page, fetch ────────────────────────
  const handleTabClick = (seniorityId) => {
    if (seniorityId === seniority || fetchingRef.current) return;
    setSeniority(seniorityId);
    setPageIndex(0);
    setExecutives([]);
    fetchData(seniorityId, 0);
  };

  // ── MRT pagination handler ─────────────────────────────────────────────────
  // MRT calls this with the full pagination object { pageIndex, pageSize }
  const handlePaginationChange = (updater) => {
    const next =
      typeof updater === "function"
        ? updater({ pageIndex, pageSize })
        : updater;
    if (next.pageIndex !== pageIndex) {
      setPageIndex(next.pageIndex);
      fetchData(seniority, next.pageIndex);
    }
  };

  // ── Reveal email / phone ───────────────────────────────────────────────────
  const fetchExContact = async (para, contactId) => {
    try {
      setGlobalLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/lusha/fetch_contact_details`,
        { requestId, contactIds: contactId, parameter: para },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = response.data?.lushares;
      if (data) {
        const emails =
          data?.emailAddresses?.map((e) => e.email).join(", ") || "";
        const phone = data?.phoneNumbers?.map((p) => p.number).join(", ") || "";
        if (para === "revealEmails" && emails) {
          contact_details({
            title: "Emails",
            text: emails,
            icon: "info",
            confirmButtonText: "Copy To Clipboard",
            confirmCallback: () => CopyDetails(emails),
          });
        } else if (para === "revealPhones" && phone) {
          contact_details({
            title: "Phone Numbers",
            text: phone,
            icon: "info",
            confirmButtonText: "Copy To Clipboard",
            confirmCallback: () => CopyDetails(phone),
          });
        } else {
          contact_details({
            title: "Oops!",
            text: "No Data Found",
            icon: "error",
            confirmButtonText: "Okay",
          });
        }
      } else {
        contact_details({
          title: "Oops!",
          text: "No Data Found",
          icon: "error",
          confirmButtonText: "Okay",
        });
      }
    } catch (error) {
      console.error(error);
      toastr.error(error.message);
    } finally {
      setGlobalLoading(false);
    }
  };

  const CopyDetails = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toastr.success("Copied to clipboard");
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const BuildRelation = async (contactId) => {
    try {
      setGlobalLoading(true);
      const enrichResponse = await axios.post(
        `${API_BASE_URL}/lusha/prospecting_contact_enrich`,
        { requestId, contactIds: contactId, parameter: "revealEmails" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastr.success(
        enrichResponse.data?.message || "Contact added successfully",
      );
    } catch (error) {
      console.error(error);
      toastr.error(error.message);
    } finally {
      setGlobalLoading(false);
    }
  };

  const excolumns = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "jobTitle", header: "Designation" },
    {
      accessorKey: "contactId",
      header: "Action",
      Cell: ({ row }) => (
        <ul className="contact-icon d-flex align-items-center">
          <li>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                fetchExContact("revealEmails", row.original.contactId);
              }}
              title="Reveal Email"
              className="p-1 rounded-circle contact-icon-mail d-flex align-items-center justify-content-center"
            >
              <span className="d-flex align-items-center justify-content-center">
                <i className="ti ti-mail text-gray-5"></i>
              </span>
            </a>
          </li>
          <li>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                fetchExContact("revealPhones", row.original.contactId);
              }}
              title="Reveal Phone"
              className="p-1 rounded-circle contact-icon-call d-flex align-items-center justify-content-center"
            >
              <span className="d-flex align-items-center justify-content-center">
                <i className="ti ti-phone-call text-gray-5"></i>
              </span>
            </a>
          </li>
          {/* <li>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                BuildRelation(row.original.contactId);
              }}
              title="Add To Contact"
              className="p-1 rounded-circle contact-icon-msg d-flex align-items-center justify-content-center"
            >
              <span className="d-flex align-items-center justify-content-center">
                <i className="fa-regular fa-handshake text-gray-5"></i>
              </span>
            </a>
          </li> */}
        </ul>
      ),
    },
  ];

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Executives
                {companyName && (
                  <span className="text-muted fs-14 ms-2">— {companyName}</span>
                )}
              </h5>
              <button
                type="button"
                onClick={onClose}
                className="btn-close"
              ></button>
            </div>
            <div className="modal-body">
              {/* Seniority tabs */}
              <div className="mb-3">
                {SENIORITY_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`btn btn-outline-primary rounded-pill ms-1 mb-1 ${seniority === tab.id ? "active" : ""}`}
                    onClick={() => handleTabClick(tab.id)}
                    disabled={loading}
                  >
                    {tab.label} — {executiveCounts[tab.countKey] ?? "…"}
                  </button>
                ))}
              </div>

              <MaterialReactTable
                columns={excolumns}
                data={executives}
                rowCount={rowCount}
                manualPagination
                onPaginationChange={handlePaginationChange}
                state={{
                  pagination: { pageIndex, pageSize },
                  isLoading: loading,
                }}
                enablePagination
              />
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default ExecutivesModalProspecting;
